/**
 * Two-slice tag reliability donut, mirroring StateDonut's shape so the
 * Fleet Health and Tag Health tiles line up visually. Just two buckets
 * — Healthy (green) and Abnormal (red) — collapsed server-side from
 * v2_tag_reliability_snapshot's four buckets.
 */
import React from "react";

const HEALTHY_HEX  = "#16a34a"; // green-600 — matches STATE_HEX.UP tone
const ATTENTION_HEX = "#dc2626"; // red-600  — matches STATE_HEX.DOWN tone

interface TagHealthDonutProps {
  healthy: number;
  /** "Needs Attention" bucket count (FLICKERY + UNRELIABLE). Field name
   *  stays `abnormal` for the API contract; the UI labels it as
   *  "Needs Attention" everywhere the user sees it. */
  abnormal: number;
  size?: number;
  thickness?: number;
  /** "tags" by default; some contexts might want "reads" etc. */
  unitLabel?: string;
}

function polar(cx: number, cy: number, r: number, angle: number): [number, number] {
  return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
}

function arcPath(
  cx: number, cy: number, rOuter: number, rInner: number,
  start: number, end: number,
): string {
  const [x1, y1] = polar(cx, cy, rOuter, start);
  const [x2, y2] = polar(cx, cy, rOuter, end);
  const [x3, y3] = polar(cx, cy, rInner, end);
  const [x4, y4] = polar(cx, cy, rInner, start);
  const largeArc = end - start > Math.PI ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

const CenterLabel: React.FC<{
  cx: number; cy: number; size: number; total: number; unitLabel: string;
}> = ({ cx, cy, size, total, unitLabel }) => {
  if (total === 0) {
    return (
      <text
        x={cx} y={cy + 6}
        textAnchor="middle"
        className="fill-secondary-400 text-base"
      >
        —
      </text>
    );
  }
  return (
    <>
      <text
        x={cx} y={cy - 4}
        textAnchor="middle"
        className="fill-secondary-900 font-semibold"
        style={{ fontSize: size * 0.18 }}
      >
        {total.toLocaleString()}
      </text>
      <text
        x={cx} y={cy + size * 0.12}
        textAnchor="middle"
        className="fill-secondary-500"
        style={{ fontSize: size * 0.06 }}
      >
        {unitLabel}
      </text>
    </>
  );
};

export const TagHealthDonut: React.FC<TagHealthDonutProps> = ({
  healthy, abnormal, size = 240, thickness = 0.45, unitLabel = "tags",
}) => {
  const total = healthy + abnormal;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4;
  const rInner = rOuter * (1 - thickness);
  const rMid = (rOuter + rInner) / 2;
  const strokeWidth = rOuter - rInner;
  const label = (
    <CenterLabel cx={cx} cy={cy} size={size} total={total} unitLabel={unitLabel} />
  );

  if (total === 0) {
    return (
      <svg width={size} height={size} role="img" aria-label="No tag data">
        <circle
          cx={cx} cy={cy} r={rMid}
          fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}
        />
        {label}
      </svg>
    );
  }

  // Single-bucket short-circuit — same rationale as StateDonut: a full
  // 360° arc renders as nothing in most SVG engines because start==end.
  if (healthy === 0 || abnormal === 0) {
    const color = abnormal === 0 ? HEALTHY_HEX : ATTENTION_HEX;
    return (
      <svg width={size} height={size} role="img" aria-label="Tag health donut">
        <circle
          cx={cx} cy={cy} r={rMid}
          fill="none" stroke={color} strokeWidth={strokeWidth}
        />
        {label}
      </svg>
    );
  }

  const healthySweep = (healthy / total) * Math.PI * 2;
  const start = -Math.PI / 2;
  const mid = start + healthySweep;
  const end = start + Math.PI * 2;

  return (
    <svg width={size} height={size} role="img" aria-label="Tag health donut">
      <path d={arcPath(cx, cy, rOuter, rInner, start, mid)} fill={HEALTHY_HEX}>
        <title>{`Healthy: ${healthy.toLocaleString()}`}</title>
      </path>
      <path d={arcPath(cx, cy, rOuter, rInner, mid, end)} fill={ATTENTION_HEX}>
        <title>{`Needs attention: ${abnormal.toLocaleString()}`}</title>
      </path>
      {label}
    </svg>
  );
};

export interface TagHealthLegendProps {
  healthy: number;
  abnormal: number;
}

export const TagHealthLegend: React.FC<TagHealthLegendProps> = ({
  healthy, abnormal,
}) => {
  const total = healthy + abnormal;
  const pct = (n: number) => (total === 0 ? "—" : `${((n / total) * 100).toFixed(1)}%`);
  return (
    <ul className="text-sm text-secondary-800 space-y-2 min-w-40">
      <li className="flex items-center gap-2">
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ backgroundColor: HEALTHY_HEX }}
          aria-hidden
        />
        <span className="flex-1">Healthy</span>
        <span className="tabular-nums text-secondary-900">
          {healthy.toLocaleString()}
        </span>
        <span className="tabular-nums text-secondary-500 w-14 text-right">
          {pct(healthy)}
        </span>
      </li>
      <li className="flex items-center gap-2">
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ backgroundColor: ATTENTION_HEX }}
          aria-hidden
        />
        <span className="flex-1">Needs Attention</span>
        <span className="tabular-nums text-secondary-900">
          {abnormal.toLocaleString()}
        </span>
        <span className="tabular-nums text-secondary-500 w-14 text-right">
          {pct(abnormal)}
        </span>
      </li>
    </ul>
  );
};

export default TagHealthDonut;
